import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Button } from '@/components/atoms/button'
import { DailySummary } from '@/models/sales-manager/detail'

type ConfirmModalProps = {
  salesTotalDetail: DailySummary
  onClickModalConfirm: (date: string, startDatePeriod: string) => void
  onClickModalCancel: () => void
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({ salesTotalDetail, onClickModalConfirm, onClickModalCancel }) => {
  const { t } = useTranslation()
  const isUnpaid = salesTotalDetail.indicator.unpaidTotal > 0

  return (
    <div css={modalWrapperStyle}>
      <div css={modalContentStyle}>
        <p css={modalTitleStyle}>{t('Are you sure about the following contents')}</p>
        <div css={confirmContentStyle}>
          <p css={confirmContentDateStyle}>{dayjs(salesTotalDetail.sales.summaryFrom).format(t('MM-DD-YYYY'))}</p>
          <dl>
            <div css={confirmContentItemStyle}>
              <dt css={confirmContentItemTermStyle}>{t('Actual sales')}</dt>
              <dd css={confirmContentItemTextStyle}>¥{salesTotalDetail.indicator.salesTotal.toLocaleString() || '-'}</dd>
            </div>
            <div css={confirmContentItemStyle}>
              <dt css={confirmContentItemTermStyle}>{t('Today’s Deposits')}</dt>
              <dd css={confirmContentItemTextStyle}>¥{salesTotalDetail.indicator.depositTotal.toLocaleString() || '-'}</dd>
            </div>
            <div css={confirmContentItemStyle}>
              <dt css={[confirmContentItemTermStyle, isUnpaid && unpaidStyle]}>{t('Amount to be collected')}</dt>
              <dd css={[confirmContentItemTextStyle, isUnpaid && unpaidStyle]}>
                ¥{salesTotalDetail.indicator.unpaidTotal.toLocaleString()}
              </dd>
            </div>
          </dl>
        </div>
        <div css={footerButtonWrapperStyle}>
          <Button width={185} height={38} marginRight={30} buttonType={3} onClick={onClickModalCancel}>
            {t('Cancel')}
          </Button>
          <Button
            width={185}
            height={38}
            buttonType={1}
            onClick={() => onClickModalConfirm(salesTotalDetail.sales.summaryFrom, salesTotalDetail.payments.summaryFrom!)}
          >
            {t('Confirm')}
          </Button>
        </div>
      </div>
    </div>
  )
}

const modalWrapperStyle = css({
  boxShadow: '0px 0px 6px #0000001A',
  border: '1px solid #F2F2F2',
  borderRadius: 5,
  marginRight: 16,
  marginBottom: 16,
  width: '100vw',
  height: '100vh',
  background: 'rgba(39, 39, 39, 0.5)',
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: 103,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

const modalContentStyle = css({
  width: 688,
  height: 383,
  padding: '45px 0',
  background: '#fff',
  borderRadius: 5,
})

const modalTitleStyle = css({
  marginBottom: 27,
  fontSize: 18,
  letterSpacing: 2.7,
  textAlign: 'center',
  fontWeight: 'bold',
})

const confirmContentStyle = css({
  width: 621,
  padding: 32,
  margin: '0 auto 30px',
  background: '#f5f5f5',
  border: '1px solid #ccc',
})

const confirmContentDateStyle = css({
  marginBottom: 16,
  fontSize: 18,
  fontWeight: 'bold',
  letterSpacing: 2.7,
})

const confirmContentItemStyle = css({
  marginBottom: 17,
  display: 'flex',
  '&:last-child': {
    marginBottom: 0,
  },
})

const confirmContentItemTextStyle = css({
  fontSize: 16,
  letterSpacing: 2.4,
})

const confirmContentItemTermStyle = css(confirmContentItemTextStyle, { width: 140 })

const unpaidStyle = css({ color: '#ff5500' })

const footerButtonWrapperStyle = css({
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'center',
})
